import { Component, OnDestroy, OnInit, Output, EventEmitter, ViewChild } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatChipGrid, MatChipInput, MatChipRemove, MatChipRow } from '@angular/material/chips';
import { MatIcon } from '@angular/material/icon';
import { forkJoin, Subject, Subscription } from 'rxjs';
import { Entities } from '../../data-rules/data-rules-model';
import { ICustomPositioningConfig } from '../../data-rules/lib/custom-component-overlay.directive';
import { IEntityItem, PairedAutocompleteComponent } from '../../data-rules/lib/paired-autocomplete/paired-autocomplete.component';
import { StaticOptionsProvider } from '../../data-rules/lib/option-items-providers';
import { MetaDataService, MetaSource } from '../../data-rules/meta-data.service';
import { appearWidth } from '../../data-rules/rule/kyloe-animations';
import { LoadingComponent } from '../../loading/loading.component';
import { MessageNotificationService } from '../../shared/services/message-notification.service';
import { DEFAULT_SOURCE_FIELD_LABELS, EnrichmentSourceFieldsService } from './enrichment-source-fields.service';

interface IAvailableField {
  fieldName: string;
  fieldLabel: string;
}

@Component({
  selector: 'app-enrichment-source-fields',
  templateUrl: './enrichment-source-fields.component.html',
  styleUrls: ['./enrichment-source-fields.component.scss'],
  animations: [appearWidth],
  imports: [
    ReactiveFormsModule, PairedAutocompleteComponent,
    MatChipGrid, MatChipRow, MatChipRemove, MatChipInput, MatIcon, LoadingComponent
  ]
})
export class EnrichmentSourceFieldsComponent implements OnInit, OnDestroy {

  @Output()
  needsSavingEmitter: EventEmitter<boolean> = new EventEmitter<boolean>();

  loading = true;
  availableFields: IAvailableField[] = [];
  selectedFields: string[] = [];
  selectedFieldsWithLabels: { fieldName: string; label: string }[] = [];

  readonly primaryEntity: Entities = 'Candidate' as Entities;
  readonly dataSourceType = MetaSource.DATA_SOURCE;
  readonly entitiesOptions: IEntityItem[] = [
    { value: 'Candidate', label: 'Candidate', secondaryDescriptor: null, description: 'Candidate' }
  ];
  readonly overlayPositioningConfig: ICustomPositioningConfig = {
    originX: 'center',
    originY: 'bottom',
    overlayX: 'center',
    overlayY: 'top',
    offsetY: 8,
    offsetX: -20
  };

  addFieldControl = new FormControl<string | null>(null);
  addFieldEntityControl = new FormControl<string | null>(null);
  private clearAddField = new Subject<void>();
  readonly clearAddField$ = this.clearAddField.asObservable();

  private addFieldPicker: PairedAutocompleteComponent;
  private subscriptions: Subscription[] = [];

  @ViewChild(PairedAutocompleteComponent)
  set addFieldPickerRef(picker: PairedAutocompleteComponent | undefined) {
    if (picker) {
      this.addFieldPicker = picker;
      this.applyPickerOptions();
    }
  }

  constructor(
    private service: EnrichmentSourceFieldsService,
    private messageNotificationService: MessageNotificationService,
    private metaDataService: MetaDataService
  ) {}

  ngOnInit(): void {
    forkJoin({
      meta: this.metaDataService.getAllMeta(),
      available: this.service.getAvailableFields(),
      selected: this.service.getSourceFields()
    }).subscribe({
      next: ({ available, selected }) => {
        this.availableFields = available.sort((a, b) => {
          const labelA = a.fieldLabel ?? a.fieldName;
          const labelB = b.fieldLabel ?? b.fieldName;
          return labelA.localeCompare(labelB);
        });
        this.selectedFields = [...selected];
        this.rebuildLabels();
        this.loading = false;
        this.applyPickerOptions();
      },
      error: (err) => {
        console.error(err);
        this.loading = false;
        this.messageNotificationService.error(
          'There was an error loading the available Bullhorn fields. Please try reloading the page.',
          err
        );
      }
    });

    this.subscriptions.push(this.addFieldControl.valueChanges.subscribe(fieldName => {
      if (fieldName) {
        this.onAddField(fieldName);
        this.clearAddField.next();
      }
    }));
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  onAddField(fieldName: string): void {
    if (fieldName && !this.selectedFields.includes(fieldName)) {
      this.selectedFields.push(fieldName);
      this.rebuildLabels();
      this.onChange();
    }
  }

  removeField(index: number): void {
    this.selectedFields.splice(index, 1);
    this.rebuildLabels();
    this.onChange();
  }

  onChange(): void {
    this.needsSavingEmitter.emit(true);
  }

  private rebuildLabels(): void {
    this.selectedFieldsWithLabels = this.selectedFields.map(fn => ({
      fieldName: fn,
      label: this.availableFields.find(f => f.fieldName === fn)?.fieldLabel ?? DEFAULT_SOURCE_FIELD_LABELS[fn] ?? fn
    }));
  }

  private applyPickerOptions(): void {
    if (!this.addFieldPicker) {
      return;
    }
    const options = this.availableFields.map(f => ({ name: f.fieldName, label: f.fieldLabel ?? f.fieldName }));
    this.addFieldPicker.setOptionsProvider(new StaticOptionsProvider(options));
  }
}
